import {
  createTheme,
  Container,
  Paper,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Avatar,
  Button,
} from "@mui/material"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import T from "../hooks/Language/T"
import ShariLogo from "../../assets/images/shariehandslogo.png"
import { useApiService } from "../hooks/Api/useApiService"
import useError from "../hooks/Error/useError"

export default function PrivacyPolicy({
  appName = "Sharie",
  contactEmail = "wishgoods@gmail.com",
  lastUpdated = "18/08/2025",
}) {
  const { postData } = useApiService()
  const { Error: ErrorAlert, success, error } = useError()

  const onAccept = () => {
    postData("users/privacypolicy", { user_name: localStorage.getItem("user") }).then((data) => {
      if (data.message === "Successful") success("Cookies accepted successfully!")
      else error("Failed to accept cookies. Please try again later.")
    })
  }
  return (
    <T>
      <Container maxWidth='md' sx={{ py: { xs: 3, md: 6 } }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            border: (t) => `1px solid ${t.palette.primary.main}20`,
          }}
        >
          {/* RTL wrapper */}
          <Box sx={{ direction: "rtl", textAlign: "right" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 2,
              }}
            >
              <Typography
                variant='h3'
                component='h1'
                sx={{ fontWeight: 800, color: "primary.main" }}
              >
                {"מדיניות פרטיות -" + appName}
              </Typography>
              <Avatar src={ShariLogo} alt='logo' />
            </Box>

            <Typography variant='subtitle1' sx={{ mb: 3, color: "text.secondary" }}>
              {"עודכן לאחרונה : " + lastUpdated}
            </Typography>

            <Typography variant='body1' sx={{ mb: 3 }}>
              ברוכים הבאים ל- Sharie. רשת לשיתוף ולמידת כישורים. אנו מעריכים את פרטיותכם ומתחייבים
              להגן על המידע האישי שלכם. להלן פירוט האופן בו אנו אוספים, משתמשים, שומרים ומגנים על
              המידע בעת השימוש בשירות.
            </Typography>

            <SectionTitle>א. איזה מידע אנו אוספים</SectionTitle>
            <List dense sx={{ mb: 3 }}>
              <PolicyItem>פרטי חשבון: שם, כתובת אימייל, פרטי התחברות.</PolicyItem>
              <PolicyItem>תוכן משתמשים: כישורים, פוסטים, תגובות, הודעות.</PolicyItem>
              <PolicyItem>מידע טכני: כתובת IP, סוג דפדפן, מערכת הפעלה, נתוני שימוש.</PolicyItem>
              <PolicyItem>עוגיות (Cookies): לשיפור חוויית המשתמש ושמירת העדפות.</PolicyItem>
            </List>

            <SectionTitle>ב. כיצד אנו משתמשים במידע</SectionTitle>
            <List dense sx={{ mb: 3 }}>
              <PolicyItem>{" מתן וגישה לשירותי" + " " + appName + "."}</PolicyItem>
              <PolicyItem>התאמת החוויה האישית ושיפור השירות.</PolicyItem>
              <PolicyItem>שליחת תקשורת תפעולית ועדכונים על השירות.</PolicyItem>
              <PolicyItem>שיפור ביצועים ותיקון בעיות טכניות.</PolicyItem>
              <PolicyItem>אבטחת מידע ומניעת שימוש לרעה.</PolicyItem>
            </List>

            <SectionTitle>ג. שיתוף מידע עם צדדים שלישיים</SectionTitle>
            <Typography variant='body1' sx={{ mb: 1.5 }}>
              אנו עשויים לשתף מידע במקרים הבאים בלבד:
            </Typography>
            <List dense sx={{ mb: 3 }}>
              <PolicyItem>
                ספקי שירות טכנולוגיים (כגון Render, Vercel, MongoDB Atlas) הדרושים להפעלת המערכת.
              </PolicyItem>
              <PolicyItem>ציות לחוק - כאשר הדבר נדרש לפי חוק, תקנה או צו שיפוטי.</PolicyItem>
              <PolicyItem>
                {"הגנה על " + appName + "- במקרה הצורך להגן על זכויותינו, המשתמשים שלנו או הציבור."}
              </PolicyItem>
            </List>
            <InfoNote>איננו מוכרים או סוחרים במידע אישי לצדדים שלישיים לצרכי פרסום.</InfoNote>

            <SectionTitle>ד. אבטחת מידע</SectionTitle>
            <Typography variant='body1' sx={{ mb: 3 }}>
              אנו מיישמים אמצעים טכנולוגיים וארגוניים לשמירה על המידע האישי. עם זאת, אין באפשרותנו
              להבטיח אבטחה מוחלטת, ולכן מומלץ לנקוט זהירות בעת שיתוף מידע רגיש.
            </Typography>

            <SectionTitle>ה. שמירת מידע</SectionTitle>
            <Typography variant='body1' sx={{ mb: 3 }}>
              המידע נשמר כל עוד הוא נדרש לצורך מתן השירות או בהתאם לדרישות הדין. תוכלו לבקש מחיקה או
              עדכון של המידע האישי שלכם, כמפורט להלן.
            </Typography>

            <SectionTitle>ו. זכויות המשתמשים</SectionTitle>
            <List dense sx={{ mb: 3 }}>
              <PolicyItem>זכות לגישה לעיון במידע האישי שנשמר אודותיכם.</PolicyItem>
              <PolicyItem>בקשה לתיקון או עדכון מידע שגוי.</PolicyItem>
              <PolicyItem>בקשה למחיקת המידע, בכפוף לחובות משפטיות לשימורו.</PolicyItem>
              <PolicyItem>התנגדות לעיבוד המידע או בקשה להגבלת שימוש בו.</PolicyItem>
            </List>

            <SectionTitle>ז. יצירת קשר</SectionTitle>
            <Typography variant='body1' sx={{ mb: 1.5 }}>
              לשאלות, בירורים או מימוש זכויותיכם, ניתן לפנות אלינו בכתובת:
              <Link href={`mailto:${contactEmail}`} underline='hover' color='secondary'>
                {contactEmail}
              </Link>
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant='caption' sx={{ color: "text.secondary" }}>
              הערה: מסמך זה מהווה טיוטה כללית ואינו ייעוץ משפטי. מומלץ להתייעץ עם עו"ד לשם התאמה
              מלאה לדין החל (כולל GDPR ותקנות מקומיות בישראל).
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button variant='contained' color='primary' onClick={onAccept}>
              קיבלתי
            </Button>
          </Box>
          <ErrorAlert />
        </Paper>
      </Container>
    </T>
  )
}

function SectionTitle({ children }) {
  return (
    <>
      <Typography
        variant='h5'
        sx={{
          mt: 3,
          mb: 1.5,
          fontWeight: 800,
          color: "primary.main",
        }}
      >
        {children}
      </Typography>
    </>
  )
}

function PolicyItem({ children }) {
  return (
    <ListItem sx={{ px: 0 }}>
      <ListItemIcon sx={{ minWidth: 36 }}>
        <CheckCircleOutlineIcon color='secondary' />
      </ListItemIcon>
      <ListItemText
        primaryTypographyProps={{ variant: "body1" }}
        primary={children}
        sx={{ m: 0 }}
      />
    </ListItem>
  )
}

function InfoNote({ children }) {
  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        borderRadius: 2,
        bgcolor: (t) => t.palette.primary.main + "0D", // light blue tint
        border: (t) => `1px dashed ${t.palette.primary.main}55`,
      }}
    >
      <Typography variant='body2'>{children}</Typography>
    </Box>
  )
}
